import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  decodeDocumentIdFromRelationshipTreeRowId,
  GROUP_ROW_FIELD_KEY_SEPARATOR,
  GROUP_ROW_PATH_SEPARATOR,
  isUUIDV4,
} from '@aline/common';
import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type {
  GridApiPremium,
  GridGroupNode,
  GridRowId,
  GridRowModel,
  GridTreeNode,
} from '@mui/x-data-grid-premium';
import {
  gridClasses,
  gridDataRowIdsSelector,
  gridRowSelectionIdsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { uniq } from 'lodash';
import { omit } from 'ramda';
import { getIsGroupHeaderRow } from 'src/components/reports/types';
import { useDebouncedCallback } from 'use-debounce';

/**
 * Shares the Filters button element between a list page (which renders the
 * DataGridPremium) and the toolbar slot (which renders `FiltersToolbarButton`).
 * The parent feeds the captured element into `slotProps.panel.target` so MUI's
 * Popper anchors the filter panel right under the Filters button instead of
 * the default position under the first column header. The button registers
 * itself via a ref callback from `useFiltersAnchorRef()`.
 *
 * Usage:
 * ```tsx
 * function MyList() {
 *   const { anchorEl, setAnchorEl } = useFiltersAnchorState();
 *   return (
 *     <FiltersAnchorProvider setAnchorEl={setAnchorEl}>
 *       <DataGridPremium
 *         slotProps={{
 *           panel: { target: anchorEl ?? undefined, placement: 'bottom-start' },
 *           ...
 *         }}
 *         ...
 *       />
 *     </FiltersAnchorProvider>
 *   );
 * }
 * // Inside FiltersToolbarButton: the composed ref calls setAnchorEl.
 * ```
 */
export interface FiltersAnchorCtx {
  setAnchorEl: (el: HTMLElement | null) => void;
}
const FiltersAnchorContext = createContext<FiltersAnchorCtx | null>(null);

export function FiltersAnchorProvider({
  children,
  setAnchorEl,
}: {
  children: React.ReactNode;
  setAnchorEl: FiltersAnchorCtx['setAnchorEl'];
}) {
  const value = useMemo(() => ({ setAnchorEl }), [setAnchorEl]);
  return <FiltersAnchorContext.Provider value={value}>{children}</FiltersAnchorContext.Provider>;
}

export function useFiltersAnchorRef(): (el: HTMLElement | null) => void {
  const ctx = useContext(FiltersAnchorContext);
  return ctx?.setAnchorEl ?? noopRefCallback;
}

function noopRefCallback(_el: HTMLElement | null): void {
  // no-op — used when FiltersToolbarButton renders outside a FiltersAnchorProvider.
}

export function useFiltersAnchorState(): {
  anchorEl: HTMLElement | null;
  setAnchorEl: FiltersAnchorCtx['setAnchorEl'];
} {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  return { anchorEl, setAnchorEl };
}

/**
 * For future reference, all DataGrid styles are defined here.
 * https://github.com/mui-org/material-ui-x/blob/master/packages/grid/_modules_/grid/components/containers/GridRootStyles.ts.
 */
export function createDataGridPremiumOverrideStyles(theme: Theme): SystemStyleObject<Theme> {
  return {
    [`& .${gridClasses.cell}`]: {
      '&:focus-within': {
        outline: 'none',
      },
      alignItems: 'center',
      borderBottom: `1px solid ${theme.appColors.rule}`,
      display: 'flex',
      py: 1,
    },
    [`& .${gridClasses.columnHeader}`]: {
      '&:focus-within': {
        outline: 'none',
      },
    },
    [`& .${gridClasses.columnHeaderTitle}`]: {
      ...omit(['lineHeight'], theme.appTypography.fieldLabel),
      fontSize: 14,
    },
    [`& .${gridClasses.columnHeaders}`]: {
      borderBottom: `1px solid ${theme.appColors.rule}`,
    },
    border: 'none',
  };
}

/**
 * Gives the grid main a paper/white background w/ a box-shadow.
 */
export const dataGridPremiumBorderRadius = 8;
export const dataGridPremiumBoxShadow =
  '0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 4px 8px 0px rgba(0, 0, 0, 0.06)';

function createDataGridPremiumPaperMainStyles(theme: Theme, footerless = false) {
  return {
    backgroundColor: theme.palette.common.white,
    borderLeft: `1px solid ${theme.appColors.rule}`,
    borderRight: `1px solid ${theme.appColors.rule}`,
    borderTop: `1px solid ${theme.appColors.rule}`,
    borderTopLeftRadius: dataGridPremiumBorderRadius,
    borderTopRightRadius: dataGridPremiumBorderRadius,
    ...(footerless
      ? {
          borderBottom: `1px solid ${theme.appColors.rule}`,
          borderBottomLeftRadius: dataGridPremiumBorderRadius,
          borderBottomRightRadius: dataGridPremiumBorderRadius,
        }
      : undefined),
    overflow: 'hidden',
  };
}

export function createDataGridPremiumPaperStyles(theme: Theme): SystemStyleObject<Theme> {
  return {
    ...createDataGridPremiumOverrideStyles(theme),
    [`&.${gridClasses.root}`]: {
      backgroundColor: 'transparent',
      overflow: 'visible',
    },
    // Main container (column headers + rows): top-rounded, borders on top/left/right
    // so it visually joins with the footer container below.
    [`& .${gridClasses.main}`]: {
      ...createDataGridPremiumPaperMainStyles(theme),
    },
    [`& .${gridClasses.columnHeaders}`]: {
      borderBottom: `1px solid ${theme.appColors.rule}`,
      borderTopLeftRadius: dataGridPremiumBorderRadius,
      borderTopRightRadius: dataGridPremiumBorderRadius,
    },
    /**
     * A side panel (Charts, Pivot) is a sibling of the main container, so the paper
     * surface has to continue into it or the panel floats on the page background.
     * The main container keeps its right border as the divider between the two and
     * gives up its right corner; the footer squares its own right corner via
     * `ListCountFooter`'s `squareRight`.
     */
    [`& .${gridClasses.sidebar}`]: {
      backgroundColor: theme.palette.common.white,
      borderBottom: `1px solid ${theme.appColors.rule}`,
      borderBottomRightRadius: dataGridPremiumBorderRadius,
      borderRight: `1px solid ${theme.appColors.rule}`,
      borderTop: `1px solid ${theme.appColors.rule}`,
      borderTopRightRadius: dataGridPremiumBorderRadius,
      boxSizing: 'border-box',
    },
    [`&.${gridClasses.withSidePanel} .${gridClasses.main}`]: {
      borderTopRightRadius: 0,
    },
    [`&.${gridClasses.withSidePanel} .${gridClasses.columnHeaders}`]: {
      borderTopRightRadius: 0,
    },
    // Footer container is a transparent pass-through — the footer component
    // (e.g. ListCountFooter) owns its own background, border, and radius so
    // the visual styling isn't dependent on MUI's default container styles.
    [`& .${gridClasses.footerContainer}`]: {
      border: 'none',
      minHeight: 0,
      padding: 0,
    },
    [`& .${gridClasses.toolbarContainer}`]: {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
  };
}

export function createDataGridPremiumFooterlessPaperStyles(theme: Theme): SystemStyleObject<Theme> {
  return {
    ...createDataGridPremiumPaperStyles(theme),
    backgroundColor: 'transparent',
    display: 'flex',
    flex: 1,
    height: '100%',
    minHeight: 0,
    overflow: 'visible',
    [`&.${gridClasses.root}`]: {
      backgroundColor: 'transparent',
      flex: 1,
      height: '100%',
      minHeight: 0,
      overflow: 'visible',
    },
    [`& .${gridClasses.main}`]: {
      ...createDataGridPremiumPaperMainStyles(theme, true),
      flex: 1,
      minHeight: 0,
    },
  };
}

export function createDataGridPremiumFooterlessOverrideStyles(
  theme: Theme
): SystemStyleObject<Theme> {
  return {
    ...createDataGridPremiumOverrideStyles(theme),
    [`& .${gridClasses.main}`]: {
      borderBottom: `1px solid ${theme.appColors.rule}`,
      borderBottomLeftRadius: dataGridPremiumBorderRadius,
      borderBottomRightRadius: dataGridPremiumBorderRadius,
    },
  };
}

/**
 * Extends the paper styles with horizontal scrolling enabled.
 * Headers remain fixed/sticky while content scrolls horizontally.
 */
export function createDataGridPremiumHorizontalScrollStyles(
  theme: Theme
): SystemStyleObject<Theme> {
  return {
    ...createDataGridPremiumPaperStyles(theme),
    // Override main container to allow horizontal overflow while preserving borders
    [`& .${gridClasses.main}`]: {
      backgroundColor: theme.palette.common.white,
      borderLeft: `1px solid ${theme.appColors.rule}`,
      borderRight: `1px solid ${theme.appColors.rule}`,
      borderTop: `1px solid ${theme.appColors.rule}`,
      borderTopLeftRadius: dataGridPremiumBorderRadius,
      borderTopRightRadius: dataGridPremiumBorderRadius,
      overflowX: 'auto',
      overflowY: 'hidden',
    },
    // Enable horizontal scrolling on the virtual scroller
    [`& .${gridClasses.virtualScroller}`]: {
      overflowX: 'auto',
    },
    // Ensure the column headers scroll horizontally in sync with the content
    // but remain visually fixed at the top (this is default MUI behavior)
    [`& .${gridClasses.columnHeaders}`]: {
      borderBottom: `1px solid ${theme.appColors.rule}`,
      borderTopLeftRadius: dataGridPremiumBorderRadius,
      borderTopRightRadius: dataGridPremiumBorderRadius,
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  };
}

export function createDataGridPremiumWFStyles(theme: Theme): SystemStyleObject<Theme> {
  return {
    [`& .${gridClasses['row--dragging']}`]: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'flex-start',
      minWidth: 240,
    },
    [`& .${gridClasses.cell}`]: {
      '&:focus-within': {
        outline: 'none',
      },
      alignItems: 'center',
      borderBottom: 'unset',
      display: 'flex',
      px: 2,
      py: 1.5,
    },
    [`& .${gridClasses.columnHeaders}`]: {
      borderBottom: `1px solid ${theme.appColors.rule}`,
    },
    [`& .${gridClasses.columnHeader}`]: {
      '&:focus-within': {
        outline: 'none',
      },
      borderBottom: 'unset',
      padding: 'unset',
      px: 2,
      py: 1,
    },
    [`& .${gridClasses.columnHeaderTitle}`]: {
      fontWeight: 600,
    },
    [`& .${gridClasses.row}`]: {
      '&:hover': {
        backgroundColor: theme.appColors.background,
      },
    },
    border: 'unset',
    borderRadius: 'unset',
  };
}

export function createDataGridPremiumPermissionListStyles(theme: Theme): SystemStyleObject<Theme> {
  return {
    ...createDataGridPremiumOverrideStyles(theme),
    [`& .${gridClasses.row}`]: {
      '&:hover': {
        backgroundColor: 'unset',
      },
    },
  };
}

export function useDataGridSearch(): {
  search: string;
  setSearch: (searchString: string) => void;
} {
  const apiRef = useGridApiContext();
  const [search, setSearch] = useState<string>('');
  const debouncedSetQuickFilter = useDebouncedCallback((searchString: string) => {
    apiRef.current.setQuickFilterValues(searchString ? searchString.split(' ') : []);
  }, 500);

  useEffect(() => {
    debouncedSetQuickFilter(search);
  }, [debouncedSetQuickFilter, search]);

  return {
    search,
    setSearch,
  };
}

export function getNonSkeletonRowIds(apiRef: GridApiPremium): string[] {
  return apiRef
    .getAllRowIds()
    .filter(id => !String(id).includes('skeleton'))
    .map(id => String(id));
}

/**
 * The document a grid row stands for, or null when it stands for none: a
 * group header, an orphan relationship row, a skeleton placeholder.
 *
 * The id alone cannot tell a group header from a document. A header is
 * `${column}:${key}`, and when the column is an extraction question the part
 * before the `:` is that question's UUID, the same shape a nested relationship
 * row decodes to (`${documentId}:${direction}`). The row model says what the
 * row is, so it is consulted before the id is decoded.
 *
 * A selected row can have no model: `keepNonExistentRowsSelected` holds a
 * ticked row in the selection after a regroup or a filter took it out of the
 * grid. Its id is then all there is, so it counts only when it cannot be a
 * header, which is when its last segment has no `:` (a flat row, or a leaf
 * under a group). That keeps a ticked document that was filtered away in a
 * bulk action, and gives up a nested relationship row that left the grid.
 */
export function getDocumentIdFromGridRow(
  rowId: GridRowId,
  row: GridRowModel | null | undefined
): UUID | null {
  if (row && getIsGroupHeaderRow(row)) return null;
  if (!row && getIsLastSegmentKeyed(String(rowId))) return null;
  const documentId = decodeDocumentIdFromRelationshipTreeRowId(String(rowId));
  return isUUIDV4(documentId) ? documentId : null;
}

/**
 * Distinct document ids among `rowIds`, in row order, each row read through
 * {@link getDocumentIdFromGridRow}. Use this whenever row ids are about to be
 * sent to a GraphQL `[UUID!]!` input.
 */
export function getDocumentIdsFromGridRows(
  rowIds: readonly GridRowId[],
  getRow: (rowId: GridRowId) => GridRowModel | null | undefined
): UUID[] {
  const documentIds: UUID[] = [];
  for (const rowId of rowIds) {
    const documentId = getDocumentIdFromGridRow(rowId, getRow(rowId));
    if (documentId) documentIds.push(documentId);
  }
  return uniq(documentIds);
}

/** The slice of the grid api `getVisibleDocumentIds` reads. */
interface GridRowsReader {
  getAllRowIds: () => GridRowId[];
  getRow: (rowId: GridRowId) => GridRowModel | null;
}

/** Document ids of every loaded row: leaves under any group, flat rows, relationship rows. */
export function getVisibleDocumentIds(apiRef: GridRowsReader | null): UUID[] {
  if (!apiRef) return [];
  return getDocumentIdsFromGridRows(apiRef.getAllRowIds(), id => apiRef.getRow(id));
}

export function getIsGroupNode(rowNode: GridTreeNode): rowNode is GridGroupNode {
  return rowNode.type === 'group';
}

export function getIsRowExpanded(rowNode: GridTreeNode | null | undefined) {
  if (!rowNode || !getIsGroupNode(rowNode)) return false;
  return Boolean(rowNode.childrenExpanded);
}

export function useGetSelectedRowIds() {
  const apiRef = useGridApiContext();
  const selectionModel = useGridSelector(apiRef, gridRowSelectionIdsSelector);
  const selectedRowIds = useMemo(() => {
    return selectionModel
      .keys()
      .map(id => String(id))
      .filter(id => !String(id).includes('skeleton'))
      .toArray();
  }, [selectionModel]);

  return { selectedRowIds };
}

export function useDataGridNonSkeletonRowIds(): string[] {
  const apiRef = useGridApiContext();
  const dataRowIds = useGridSelector(apiRef, gridDataRowIdsSelector);
  return dataRowIds.filter(id => !String(id).includes('skeleton')).map(id => String(id));
}

/** Whether the row id's last path segment is `${prefix}:${suffix}`, the shape a group header has. */
function getIsLastSegmentKeyed(rowId: string): boolean {
  const lastSegment = rowId.slice(rowId.lastIndexOf(GROUP_ROW_PATH_SEPARATOR) + 1);
  return lastSegment.includes(GROUP_ROW_FIELD_KEY_SEPARATOR);
}
